/**
 * `createApiProtocolsSource` — the `metadata-registry-api` *protocol definition*
 * port over the shared `ApiClient`.
 *
 * A protocol definition is how the platform decodes one protocol (ABI fragments,
 * address book, detection rules, highlights, confidence weights). The registry
 * resolves it across the `system → customer → org → tenant → user` ladder and
 * lets a rung lay a `definition_patch` over the shared one; this port is the CRUD
 * behind the override editor. The wire shapes and the patch vetting live in
 * `@authe/fe-commons/metadata-analytics` (`protocols.ts`), so the contract is
 * shared with any other surface that authors one. Canonical `/metadata-protocols`
 * paths are rewritten to `internal/metadata-registry-api` by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import {
  type AnalyticsScope,
  type ProtocolDetail,
  type ProtocolSummary,
  buildProtocolQuery,
  buildProtocolsQuery,
  protocolOverrideDeleteRequest,
  protocolOverrideRequest,
  protocolsPath,
} from "@authe/fe-commons/metadata-analytics";

export interface ProtocolsSource {
  /** The protocol catalogue as `scope` resolves it (default: the caller's own). */
  listProtocols(scope?: AnalyticsScope): Promise<ProtocolSummary[]>;
  /** One protocol's resolved definition plus the per-rung patches behind it. */
  getProtocol(protocolId: string, scope?: AnalyticsScope): Promise<ProtocolDetail | null>;
  /** Author (create or replace) one rung's override. Patch must already be vetted. */
  saveOverride(
    protocolId: string,
    patch: Record<string, unknown>,
    scope?: AnalyticsScope,
  ): Promise<void>;
  /** Drop one rung's override, reverting that rung to what it inherits. */
  deleteOverride(protocolId: string, scope?: AnalyticsScope): Promise<void>;
}

/** Wire envelope for the collection read (`{ items, next_cursor }`). */
interface ProtocolListWire {
  items?: ProtocolSummary[];
  next_cursor?: string | null;
}

/** Wire envelope for a single-protocol read (`{ protocol }`). */
interface ProtocolItemWire {
  protocol?: ProtocolDetail;
}

export function createApiProtocolsSource(client: ApiClient): ProtocolsSource {
  /**
   * Every write lands on the same collection the editor lists from.
   *
   * `invalidateMatching`, not `invalidate`: both reads carry `?scope=…`
   * (`buildProtocolsQuery` / `buildProtocolQuery` attach `scopeQuery`), and the
   * exact-URL form keys on the resolved URL query string included — so naming
   * the bare path invalidated nothing and the editor's refetch was served the
   * pre-write definition out of the 30s GET cache.
   */
  const invalidate = (protocolId?: string): void => {
    client.invalidateMatching(protocolsPath());
    if (protocolId) client.invalidateMatching(buildProtocolQuery(protocolId).path);
  };

  return {
    async listProtocols(scope?: AnalyticsScope): Promise<ProtocolSummary[]> {
      const req = buildProtocolsQuery(scope);
      const page = unwrapData(
        await client.get<ProtocolListWire>(req.path, { query: req.query }),
      );
      return page?.items ?? [];
    },

    async getProtocol(
      protocolId: string,
      scope?: AnalyticsScope,
    ): Promise<ProtocolDetail | null> {
      const req = buildProtocolQuery(protocolId, scope);
      const body = unwrapData(
        await client.get<ProtocolItemWire>(req.path, { query: req.query }),
      );
      return body?.protocol ?? null;
    },

    async saveOverride(
      protocolId: string,
      patch: Record<string, unknown>,
      scope?: AnalyticsScope,
    ): Promise<void> {
      const req = protocolOverrideRequest(protocolId, patch, scope);
      // `unwrapData`: the client resolves an `{ok:false}` envelope, so a bare
      // `await` made `ProtocolOverridesPage`'s `.then(ok, err)` error arm
      // unreachable and announced "New transactions decoding X will use it"
      // after a 403 at that rung.
      unwrapData(await client.put(req.path, req.body, { query: req.query }));
      invalidate(protocolId);
    },

    async deleteOverride(protocolId: string, scope?: AnalyticsScope): Promise<void> {
      const req = protocolOverrideDeleteRequest(protocolId, scope);
      unwrapData(await client.delete(req.path, { query: req.query }));
      invalidate(protocolId);
    },
  };
}
